import { useEffect, useState } from 'react';
import { staffApi, formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { PlusCircle, Eye, EyeOff } from 'lucide-react';

const emptyForm = { name: '', description: '', price: '', displayOrder: 0 };

export default function ItemsTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [showHidden, setShowHidden] = useState(false);

  const load = () => staffApi.get('/items/admin').then(({ data }) => setItems(data.items));

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({ name: item.name, description: item.description || '', price: item.price, displayOrder: item.displayOrder });
    setOpen(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      if (editing) await staffApi.patch(`/items/${editing.id}`, form);
      else await staffApi.post('/items', form);
      toast.success('Add-on saved');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (item) => {
    await staffApi.patch(`/items/${item.id}`, { isActive: !item.isActive });
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          data-testid="items-show-hidden-toggle"
          onClick={() => setShowHidden((v) => !v)}
          className="text-sm text-muted-ink hover:text-charcoal flex items-center gap-1.5 transition-colors duration-150"
        >
          {showHidden ? <EyeOff size={14} /> : <Eye size={14} />}
          {showHidden ? 'Hide' : 'Show'} Hidden ({items.filter((it) => !it.isActive).length})
        </button>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="item-create-btn" onClick={openCreate} className="bg-saffron hover:bg-saffron-hover">
              <PlusCircle size={16} className="mr-2" /> New Add-on
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="item-form-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Edit Add-on' : 'Create Add-on'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input data-testid="item-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea data-testid="item-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Price per tiffin (CAD)</Label>
                  <Input type="number" data-testid="item-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Display Order</Label>
                  <Input type="number" data-testid="item-displayorder-input" value={form.displayOrder} onChange={(e) => setForm({ ...form, displayOrder: e.target.value })} />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid="item-save-btn" disabled={busy} onClick={save} className="bg-saffron hover:bg-saffron-hover">
                {busy ? 'Saving…' : 'Save Add-on'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white rounded-xl border border-border/40 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-bone/60">
              <TableHead>Order</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Price / tiffin</TableHead>
              <TableHead>Active</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.filter((it) => showHidden || it.isActive).map((it) => (
              <TableRow key={it.id} data-testid="item-row">
                <TableCell className="text-muted-ink">{it.displayOrder}</TableCell>
                <TableCell className="font-medium">{it.name}</TableCell>
                <TableCell>${Number(it.price).toFixed(2)} <span className="text-muted-ink text-xs">/ tiffin</span></TableCell>
                <TableCell>
                  <Switch data-testid={`item-active-toggle-${it.id}`} checked={it.isActive} onCheckedChange={() => toggleActive(it)} />
                </TableCell>
                <TableCell>
                  <button data-testid={`item-edit-btn-${it.id}`} onClick={() => openEdit(it)} className="text-sm text-coriander hover:underline">
                    Edit
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {items.filter((it) => showHidden || it.isActive).length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-ink py-8">No add-ons yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
